import { faArrowRightLong } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import BreadcrumbItem from 'Components/Atoms/BreadcrumbItem/BreadcrumbItem';
import Grid from 'Components/Atoms/Grid/Grid';
import IconWrapper from 'Components/Atoms/IconWrapper/IconWrapper';
import Spinner from 'Components/Atoms/Spinner/Spinner';
import Text from 'Components/Atoms/Text/Text';
import Title from 'Components/Atoms/Title/Title';
import Breadcrumbs from 'Components/Molecules/Breadcrumbs/Breadcrumbs';
import Card from 'Components/Molecules/Card/Card';
import TitleGroup from 'Components/Molecules/TitleGroup/TitleGroup';
import SettingsTemplate from 'Components/Templates/Page/Settings/SettingsTemplate';
import { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { ROUTES } from 'Services/Route/Route.types';
import EntityHelper from 'Services/Settings/Entity/EntityHelper';
import { StoreState } from 'Store/Reducers';
import { fetchEntities } from 'Store/Settings/Entities/Actions';
import { getEntitiesById, isEntityLoading } from 'Store/Settings/Entities/Selectors';
import { fetchEntityTypesByEntityId } from 'Store/Settings/EntityTypes/Actions';
import { getEntityTypesByEntityIdAsObject, getEntityTypesByIdAsObject, isEntityTypesFetching } from 'Store/Settings/EntityTypes/Selector';

const LayoutTypeMenuPage = ({
    isLoading,
    entitiesById,
    entityTypesById,
    entityTypeIdsByEntityId,
    ...props
}: LayoutTypeMenuPageProps) => {
    const { entity_id } = useParams();

    useEffect(() => {
        props.fetchEntities();
        if (entity_id) {
            props.fetchEntityTypesByEntityId(entity_id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const entity = useMemo(() => {
        if (entity_id) {
            return {
                ...entitiesById?.[entity_id],
                formattedName: EntityHelper.getDisplayValueByName(entitiesById?.[entity_id]?.name)
            };
        }

        return {};
    }, [entitiesById, entity_id]);

    const settingsMenu = useMemo(() => {
        if (!entity_id) return [];

        return entityTypeIdsByEntityId
            ?.[entity_id]
            ?.map(i => {
                const item = entityTypesById?.[i];

                return {
                    path: ROUTES.SETTINGS.ENTITIES.ENTITY.LAYOUTS.TYPES.getFullPath(entity_id, item?.id),
                    icon: `fa-solid fa-${item?.name?.charAt(0)?.toLowerCase()}`,
                    title: `Type - ${item?.name}`,
                    description: `Pas de layout aan voor type - ${item?.name}`
                };
            })
            ?.sort((a, b) => 
                (a.title > b.title)
                ? 1
                : -1
            );
    }, [entityTypesById, entityTypeIdsByEntityId,  entity_id]);

    return <SettingsTemplate
        breadcrumbs={
            <Breadcrumbs>
                <BreadcrumbItem path={ROUTES.SETTINGS.path}>Instellingen</BreadcrumbItem>
                <BreadcrumbItem path={ROUTES.SETTINGS.ENTITIES.getFullPath()}>Entiteiten</BreadcrumbItem>
                <BreadcrumbItem path={ROUTES.SETTINGS.ENTITIES.ENTITY.getFullPath(entity?.id)}>{entity?.formattedName || '...'}</BreadcrumbItem>
                <BreadcrumbItem>Layout Management</BreadcrumbItem>
            </Breadcrumbs>
        }
        description={`Instellingen voor de layout van ${(entity?.formattedName || 'oops...')}`}
    >
            { isLoading &&
                <Grid position='centered'>
                    <Spinner />
                    <Title size='small' weight='semibold'>Entiteiten laden...</Title>
                </Grid>
            }
            { !isLoading
            && (!entity?.id
            || !settingsMenu
            || settingsMenu?.length === 0)
            &&  <Grid position='centered'>
                    <Title size='small' weight='semibold'>Geen entiteit gevonden met de volgende id: {entity_id}</Title>
                </Grid>
            }
            { !isLoading
            && entity?.id
            && settingsMenu?.length > 0
            && <Grid axis='vertical'>
                    {
                        settingsMenu.map((i: any) =>
                            <Link to={i.path} style={{textDecoration: 'none'}} key={i.path} data-cy="entity-item">
                                <Card axis="horizontal" state="link">
                                    <TitleGroup iconBefore={
                                        <IconWrapper>
                                            <FontAwesomeIcon icon={i.icon} size="2x" />
                                        </IconWrapper>
                                    }>
                                        <Title
                                            size="inherit"
                                            transform='uppercase'
                                            weight='bold'
                                        >
                                            { i.title }
                                        </Title>
                                        <Text color='secondary'>Ga door naar de instellingen van {EntityHelper.getDisplayValueByName(i.name)}</Text>
                                    </TitleGroup>
                                    <FontAwesomeIcon icon={faArrowRightLong} size='2x' color='#757575' />
                                </Card>
                            </Link>
                        )
                    }
                </Grid>
            }
    </SettingsTemplate>;
}

interface Props {

}

const mapState = (state: StoreState) => ({
    isLoading: isEntityLoading(state) || isEntityTypesFetching(state),

    entitiesById: getEntitiesById(state),
    
    entityTypesById: getEntityTypesByIdAsObject(state),
    entityTypeIdsByEntityId: getEntityTypesByEntityIdAsObject(state)
});

const mapDispatch = {
    fetchEntities,
    fetchEntityTypesByEntityId
}

type LayoutTypeMenuPageProps = Props & ReturnType<typeof mapState> & typeof mapDispatch;

export default connect(mapState, mapDispatch)(LayoutTypeMenuPage);